import { Component, inject, OnDestroy, OnInit, input, output } from '@angular/core';
import { ZiroFormAbstractDirective } from '../../../../../../../forms/ziro-form-abstract.directive';
import { AdWriteDownAttribute } from '../../../../../../../shared/models/generated/ziroModels';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../../../../../forms/ziro-forms-models';
import { Observable, of, Subscription } from 'rxjs';
import { ZiroFormStateService } from '../../../../../../../forms/ziro-form-state.service';
import { HtmlInputType, ZiroTextConfig } from '../../../../../../../forms/fields/text/ziro-text.component';
import { ButtonSizes, ButtonStyles } from '../../../../../../../button/button.component';
import { ZiroIcons } from '../../../../../../../shared/models/ziro-icons.enum';
import { ZiroCheckboxButtonConfig } from '../../../../../../../forms/fields/checkbox-button/checkbox-button-field.component';
import { DefaultValueService } from '../../../../../../../shared/services/default-value.service';
import { VariableEditorType } from '../../../../../../../modals/edit-custom-input-text-modal/variable-editor.service';

@Component({
  selector: 'ziro-ad-attribute-child-form',
  templateUrl: './ad-attribute-child-form.component.html',
  styleUrls: ['./ad-attribute-child-form.component.scss'],
  standalone: false,
})
export class AdAttributeChildFormComponent
  extends ZiroFormAbstractDirective<AdWriteDownAttribute>
  implements OnInit, OnDestroy
{
  protected ziroFormsStateService: ZiroFormStateService;
  private _defaultValueService = inject(DefaultValueService);

  readonly index = input<number>();
  readonly isLast = input<boolean>();
  readonly hideClear = input<boolean>();
  readonly allAttributeNames = input<string[]>();
  readonly dynamicPreviewValue = input<string>();
  readonly clearItemWasClicked = output<number>();
  readonly addItemWasClicked = output<void>();
  previewText: string;
  buttonSizes = ButtonSizes;
  buttonStyles = ButtonStyles;
  ziroIcons = ZiroIcons;
  formConfig: ZiroFormConfig;
  private _subscriptions = new Subscription();

  ngOnInit(): void {
    const updateSub = this.ziroFormsUpdate$.subscribe((update) => {
      this.previewText = this._defaultValueService.generatePreview(
        update.new.attributeValue,
        this.dynamicPreviewValue(),
      );
    });
    this._subscriptions.add(updateSub);
    this.formConfig = {
      fields: {
        attributeName: {
          dataAutomation: 'attribute-name-field',
          componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT, tabIndex: this.index() + 1 }),
          required: true,
          validation: [
            {
              validator: (val: string) => {
                const allAttributeNames = this.allAttributeNames();
                if (
                  !!allAttributeNames &&
                  allAttributeNames.indexOf(val) !== -1 &&
                  allAttributeNames.indexOf(val) !== this.index()
                ) {
                  return ZiroFormsValidationState.INVALID;
                } else {
                  return ZiroFormsValidationState.VALID;
                }
              },
              message: 'tkey;admin.msdialplan.management.field.write_to_ad.card.duplicate_validation.text',
            },
          ],
        },
        attributeValue: {
          dataAutomation: 'attribute-value-field',
          componentConfig: new ZiroTextConfig({
            htmlInputType: HtmlInputType.TEXT,
            displayEditor: true,
            editorType: VariableEditorType.WRITE_TO_AD,
            tabIndex: this.index() + 2,
          }),
          required: true,
        },
        show: {
          dataAutomation: `attribute-show-checkbox-${this.index()}`,
          label: 'tkey;admin.msdialplan.management.field.write_to_ad.card.show_checkbox.label',
          componentConfig: new ZiroCheckboxButtonConfig({
            size: ButtonSizes.NORMAL,
            noTextWrap: true,
          }),
        },
      },
    };
  }

  ngOnDestroy(): void {
    this._subscriptions.unsubscribe();
  }

  clearItemClicked(): void {
    this.clearItemWasClicked.emit(this.index());
  }

  addItemClicked(): void {
    this.addItemWasClicked.emit();
  }

  toEntity = (form: AdWriteDownAttribute): AdWriteDownAttribute => {
    return {
      attributeValue: form.attributeValue,
      attributeName: form.attributeName,
      show: form.show,
      required: true,
    };
  };

  toFormData = (entity: AdWriteDownAttribute): AdWriteDownAttribute => {
    return {
      attributeValue: entity.attributeValue,
      attributeName: entity.attributeName,
      show: entity.show,
      required: true,
    };
  };

  protected submit(): Observable<any> {
    return of(null);
  }
}
